import React, { useState, useEffect, useCallback, useMemo } from 'react';
import usersApi from '../../api/usersApi';
import companiesApi from '../../api/companiesApi';
import companyBranchesApi from '../../api/companyBranchesApi';
import styles from './AdminsPage.module.css';

const PAGE_COPY = {
  admin: {
    title: 'Administradores',
    empty: 'No hay administradores.',
    createError: 'Error creando administrador.',
    deleteConfirm: '¿Eliminar este administrador?',
    deleteError: 'Error eliminando administrador.',
    loadError: 'Error cargando datos.',
    passwordConfirm: (username) =>
      `¿Generar una nueva contraseña genérica para ${username}?\nEl usuario deberá cambiarla en su próximo inicio de sesión.`,
  },
  nutriology: {
    title: 'Nutriólogas',
    empty: 'No hay nutriólogas.',
    createError: 'Error creando nutrióloga.',
    deleteConfirm: '¿Eliminar esta nutrióloga?',
    deleteError: 'Error eliminando nutrióloga.',
    loadError: 'Error cargando datos.',
    passwordConfirm: (username) =>
      `¿Generar una nueva contraseña genérica para ${username}?\nLa nutrióloga deberá cambiarla en su próximo inicio de sesión.`,
  },
};

const AdminsPage = ({ userRole = 'admin' }) => {
  const copy = PAGE_COPY[userRole] || PAGE_COPY.admin;
  const isNutriologyConfig = userRole === 'nutriology';
  const [admins, setAdmins] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [allBranches, setAllBranches] = useState([]);
  const [loading, setLoading] = useState(true);

  const [newName, setNewName] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [resettingPasswordId, setResettingPasswordId] = useState(null);
  const [editingNameId, setEditingNameId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [updatingNameId, setUpdatingNameId] = useState(null);

  const [passwordModal, setPasswordModal] = useState(null);

  // Modal de sucursales
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [selectedBranchIds, setSelectedBranchIds] = useState(new Set());
  const [expandedCompanies, setExpandedCompanies] = useState(new Set());
  const [updating, setUpdating] = useState(false);

  const branchesByCompany = useMemo(() => {
    const map = {};
    for (const branch of allBranches) {
      if (!branch.companyId) continue;
      if (!map[branch.companyId]) map[branch.companyId] = [];
      map[branch.companyId].push(branch);
    }
    return map;
  }, [allBranches]);

  // Solo empresas que tienen al menos una sucursal disponible
  const companiesWithBranches = useMemo(() => {
    return companies.filter(c => (branchesByCompany[c._id] || []).length > 0);
  }, [companies, branchesByCompany]);

  // Company bloqueada: la company de los branches ya seleccionados
  const lockedCompanyId = useMemo(() => {
    if (selectedBranchIds.size === 0) return null;
    const firstMatch = allBranches.find(b => selectedBranchIds.has(String(b._id)));
    return firstMatch?.companyId || null;
  }, [selectedBranchIds, allBranches]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [list, compList, branchList] = await Promise.all([
        userRole === 'nutriology' ? usersApi.getNutriologists() : usersApi.getAdmins(),
        companiesApi.getCompanies(),
        companyBranchesApi.getAll(),
      ]);
      setAdmins(list);
      setCompanies(Array.isArray(compList) ? compList : []);
      setAllBranches(Array.isArray(branchList) ? branchList : []);
    } catch (err) {
      console.error('Error cargando datos:', err);
      alert(copy.loadError);
    } finally {
      setLoading(false);
    }
  }, [copy.loadError, userRole]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAdd = async () => {
    const uname = newUsername.trim();
    const name = newName.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (isNutriologyConfig && !name) return alert('Ingresa el nombre de la nutrióloga.');
    if (!uname || !emailRegex.test(uname)) return alert('Ingresa un correo válido.');
    setCreating(true);
    try {
      const payload = { username: uname, role: userRole };
      if (isNutriologyConfig) payload.name = name;
      const { generatedPassword } = await usersApi.createUser(payload);
      setNewName('');
      setNewUsername('');
      setPasswordModal({ username: uname, password: generatedPassword });
      await loadData();
    } catch (err) {
      const msg = err.response?.data?.message || copy.createError;
      alert(msg);
    } finally {
      setCreating(false);
    }
  };

  const startNameEdit = (admin) => {
    setEditingNameId(admin._id);
    setEditingName(admin.name || '');
  };

  const cancelNameEdit = () => {
    setEditingNameId(null);
    setEditingName('');
  };

  const handleUpdateName = async (admin) => {
    const name = editingName.trim();
    if (!name) return alert('Ingresa el nombre de la nutrióloga.');
    setUpdatingNameId(admin._id);
    try {
      await usersApi.updateUser(admin._id, { name });
      await loadData();
      cancelNameEdit();
    } catch (err) {
      const msg = err.response?.data?.message || 'Error actualizando nombre.';
      alert(msg);
    } finally {
      setUpdatingNameId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(copy.deleteConfirm)) return;
    setDeletingId(id);
    try {
      await usersApi.deleteUser(id);
      await loadData();
    } catch (err) {
      alert(copy.deleteError);
    } finally {
      setDeletingId(null);
    }
  };

  const handleResetPassword = async (admin) => {
    if (!window.confirm(copy.passwordConfirm(admin.username))) return;
    setResettingPasswordId(admin._id);
    try {
      const { generatedPassword } = await usersApi.resetUserPassword(admin._id);
      setPasswordModal({ username: admin.username, password: generatedPassword });
    } catch (err) {
      alert('Error reseteando contraseña.');
    } finally {
      setResettingPasswordId(null);
    }
  };

  const openBranchModal = (admin) => {
    setEditingAdmin(admin);
    const adminBranchIds = new Set((admin.branches || []).map(String));
    setSelectedBranchIds(adminBranchIds);

    // Auto-expandir la empresa que tiene los branches seleccionados
    if (adminBranchIds.size > 0) {
      const firstBranch = allBranches.find(b => adminBranchIds.has(String(b._id)));
      if (firstBranch) {
        setExpandedCompanies(new Set([firstBranch.companyId]));
        return;
      }
    }
    // Fallback: expandir la empresa del admin si existe en el acordeón
    if (admin.company) {
      const companyInList = companiesWithBranches.find(c => c._id === admin.company);
      if (companyInList) {
        setExpandedCompanies(new Set([admin.company]));
        return;
      }
    }
    setExpandedCompanies(new Set());
  };

  const closeBranchModal = () => {
    setEditingAdmin(null);
    setSelectedBranchIds(new Set());
    setExpandedCompanies(new Set());
  };

  const toggleExpand = (companyId) => {
    setExpandedCompanies(prev => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId);
      else next.add(companyId);
      return next;
    });
  };

  const toggleBranch = (branch) => {
    const branchId = String(branch._id);
    setSelectedBranchIds(prev => {
      const next = new Set(prev);
      if (next.has(branchId)) next.delete(branchId);
      else next.add(branchId);
      return next;
    });
  };

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      await usersApi.updateUser(editingAdmin._id, { branches: [...selectedBranchIds] });
      await loadData();
      closeBranchModal();
    } catch (err) {
      const msg = err.response?.data?.message || 'Error actualizando acceso.';
      alert(msg);
    } finally {
      setUpdating(false);
    }
  };

  const adminBranchCount = (admin) => (admin.branches || []).length;

  return (
    <div className={styles.container}>
      <h2 className={styles.header}>{copy.title}</h2>

      <div className={styles.form}>
        {isNutriologyConfig && (
          <input
            type="text"
            className={styles.input}
            placeholder="Nombre"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
          />
        )}
        <input
          type="text"
          className={styles.input}
          placeholder="Correo electrónico"
          value={newUsername}
          onChange={e => setNewUsername(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleAdd()}
        />
        <button className={styles.button} onClick={handleAdd} disabled={creating}>
          {creating ? 'Creando…' : 'Agregar'}
        </button>
      </div>

      {loading ? (
        <p>Cargando...</p>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              {isNutriologyConfig && <th>Nombre</th>}
              <th>Usuario</th>
              <th>Sucursales con acceso</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {admins.length > 0 ? admins.map(admin => (
              <tr key={admin._id}>
                {isNutriologyConfig && (
                  <td>
                    {editingNameId === admin._id ? (
                      <div className={styles.inlineEdit}>
                        <input
                          type="text"
                          className={`${styles.input} ${styles.inlineInput}`}
                          value={editingName}
                          onChange={e => setEditingName(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleUpdateName(admin);
                            if (e.key === 'Escape') cancelNameEdit();
                          }}
                        />
                        <button
                          className={`${styles.button} ${styles.smallButton}`}
                          onClick={() => handleUpdateName(admin)}
                          disabled={updatingNameId === admin._id}
                        >
                          {updatingNameId === admin._id ? 'Guardando…' : 'Guardar'}
                        </button>
                        <button
                          className={`${styles.button} ${styles.secondaryButton} ${styles.smallButton}`}
                          onClick={cancelNameEdit}
                          disabled={updatingNameId === admin._id}
                        >
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      <div className={styles.nameCell}>
                        <span>{admin.name || 'Sin nombre'}</span>
                        <button
                          className={`${styles.button} ${styles.secondaryButton} ${styles.smallButton}`}
                          onClick={() => startNameEdit(admin)}
                        >
                          Actualizar
                        </button>
                      </div>
                    )}
                  </td>
                )}
                <td>{admin.username}</td>
                <td>{adminBranchCount(admin)}</td>
                <td className={styles.actions}>
                  <button
                    className={styles.button}
                    onClick={() => openBranchModal(admin)}
                  >
                    Sucursales
                  </button>
                  <button
                    className={`${styles.button} ${styles.secondaryButton}`}
                    onClick={() => handleResetPassword(admin)}
                    disabled={resettingPasswordId === admin._id}
                  >
                    {resettingPasswordId === admin._id ? 'Generando…' : 'Contraseña'}
                  </button>
                  <button
                    className={`${styles.button} ${styles.deleteButton}`}
                    onClick={() => handleDelete(admin._id)}
                    disabled={deletingId === admin._id}
                  >
                    {deletingId === admin._id ? 'Eliminando…' : 'Eliminar'}
                  </button>
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan={isNutriologyConfig ? 4 : 3} style={{ textAlign: 'center' }}>
                  {copy.empty}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {/* Modal: contraseña generada */}
      {passwordModal && (
        <div className={styles.modalOverlay} onClick={() => setPasswordModal(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>Contraseña generada</h3>
            <p className={styles.modalText}>
              Anota y comparte esta contraseña con <strong>{passwordModal.username}</strong>.
              En su próximo inicio de sesión se le pedirá cambiarla.
            </p>
            <div className={styles.passwordBox}>
              <strong>{passwordModal.password}</strong>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.button} onClick={() => setPasswordModal(null)}>
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: gestión de sucursales */}
      {editingAdmin && (
        <div className={styles.modalOverlay} onClick={closeBranchModal}>
          <div className={`${styles.modal} ${styles.branchModal}`} onClick={e => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>
              Sucursales — {editingAdmin.username}
            </h3>

            {lockedCompanyId && (
              <p className={styles.lockHint}>
                Solo se pueden seleccionar sucursales de una misma empresa. Deselecciona todas para cambiar de empresa.
              </p>
            )}

            <div className={styles.companyAccordion}>
              {companiesWithBranches.length > 0 ? companiesWithBranches.map(company => {
                const compBranches = branchesByCompany[company._id] || [];
                const isLocked = lockedCompanyId !== null && lockedCompanyId !== company._id;
                const isExpanded = expandedCompanies.has(company._id);
                const selectedCount = compBranches.filter(b => selectedBranchIds.has(String(b._id))).length;

                return (
                  <div
                    key={company._id}
                    className={`${styles.companySection} ${isLocked ? styles.lockedSection : ''}`}
                  >
                    <button
                      className={styles.companyHeader}
                      onClick={() => toggleExpand(company._id)}
                      type="button"
                    >
                      <span className={styles.companyName}>{company.name}</span>
                      <span className={styles.branchCount}>
                        {selectedCount}/{compBranches.length} sucursales
                      </span>
                      <span className={styles.chevron}>{isExpanded ? '▲' : '▼'}</span>
                    </button>

                    {isExpanded && (
                      <div className={styles.branchList}>
                        {compBranches.length > 0 ? compBranches.map(branch => (
                          <label
                            key={branch._id}
                            className={`${styles.branchItem} ${isLocked ? styles.disabledItem : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedBranchIds.has(String(branch._id))}
                              onChange={() => { if (!isLocked) toggleBranch(branch); }}
                              disabled={isLocked}
                            />
                            <span>{branch.name}</span>
                          </label>
                        )) : (
                          <p className={styles.noBranches}>Sin sucursales registradas.</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              }) : (
                <p>No hay sucursales disponibles.</p>
              )}
            </div>

            <div className={styles.modalFooter}>
              <button className={styles.button} onClick={handleUpdate} disabled={updating}>
                {updating ? 'Actualizando…' : 'Guardar'}
              </button>
              <button
                className={`${styles.button} ${styles.secondaryButton}`}
                onClick={closeBranchModal}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminsPage;
